import { AlertBanner } from 'components/shared/AlertBanner/AlertBanner';
import { BlotterPanelContext } from 'contexts/blotters/BlotterPanelContext';
import { useWorkflowErrorsUtils } from 'hooks/useWorkflowErrorsUtils';
import type { BlotterType } from 'models/blotters/BlotterType';
import type { IBlotterItem } from 'models/IBlotterItem';
import { BackendError } from 'models/NextWorkflowError';
import { FC, useMemo, useState } from 'react';

import { BlotterPanelBody } from './BlotterPanelBody/BlotterPanelBody.connect';
import { BlotterPanelFooter } from './BlotterPanelFooter/BlotterPanelFooter.connect';
import { BlotterPanelFooterSkeleton } from './BlotterPanelFooter/BlotterPanelFooter.skeleton';
import type { BlotterPanelTabType } from './BlotterPanelHeader/BlotterPanelHeader';
import { BlotterPanelHeader } from './BlotterPanelHeader/BlotterPanelHeader.connect';

export interface BlotterPanelStateProps {
  blotterItem?: IBlotterItem;
  blotterType: BlotterType;
  isFetching: boolean;
  isFetchingFailed: boolean;
  fetchErrorDetails?: BackendError;
  isShowFailedAlert: boolean;
}

export type BlotterPanelDispatchProps = {
  onDismissAlertClicked: () => void;
};

export type BlotterPanelProps = BlotterPanelStateProps & BlotterPanelDispatchProps;

export const BlotterPanelComponent: FC<BlotterPanelProps> = ({
  blotterItem,
  blotterType,
  isFetching,
  isFetchingFailed,
  isShowFailedAlert,
  fetchErrorDetails,
  onDismissAlertClicked,
}) => {
  const { getWorkflowErrorMessage } = useWorkflowErrorsUtils();
  const [activeTab, setActiveTab] = useState<BlotterPanelTabType>('SUMMARY');
  const msg =
    isFetchingFailed && fetchErrorDetails !== undefined
      ? fetchErrorDetails.rejections.map(r => getWorkflowErrorMessage(r))
      : ['unknownErrorContactSale'];

  const contextProps = useMemo(
    () => ({
      activeTab,
      blotterType,
      setActiveTab,
      blotterItem,
    }),
    [activeTab, blotterItem, blotterType],
  );

  const isLoading = useMemo(() => isFetching || blotterItem === undefined, [isFetching, blotterItem]);

  const blotterPanelFooter = isLoading ? (
    <BlotterPanelFooterSkeleton blotterType={blotterType} />
  ) : (
    <BlotterPanelFooter blotterItem={blotterItem!} blotterType={blotterType} />
  );

  return (
    <BlotterPanelContext.Provider value={contextProps}>
      <div className="card bg-lvl1 h-100 border-start border-top d-flex flex-column" data-e2e="blotter-panel">
        <BlotterPanelHeader blotterItem={blotterItem!} blotterType={blotterType} isLoading={isLoading} />
        <BlotterPanelBody blotterItem={blotterItem!} />
        {isFetchingFailed && isShowFailedAlert ? (
          <AlertBanner
            e2e="obsolete-quote-alert"
            color="warning"
            className="position-relative bottom-0 start-0 end-0"
            messages={msg}
            icon="error_outline"
            dismissible
            onDismissed={onDismissAlertClicked}
          />
        ) : (
          blotterPanelFooter
        )}
      </div>
    </BlotterPanelContext.Provider>
  );
};

BlotterPanelComponent.displayName = 'BlotterPanelComponent';
